import { Button } from "@instacover/ui"
import { isNotNil } from "@instacover/utils"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import { useTranslation } from "react-i18next"
import { useNetworkState, useWindowScroll } from "react-use"

import { useFinishSession } from "@/api/hooks"
import { isRealOrderedStep, useOrderedSteps, useUploadStatus } from "@/services/dexie"
import { logger } from "@/utils/monitoring/logging"

import { BaseLayout } from "../../../components/layouts/BaseLayout"
import { SpinnerLayout } from "../../../components/layouts/SpinnerLayout"
import { resetFailedUploads } from "../../../helpers/hooks/useStore"
import { IconCam } from "../../../images/xd/icons/IconCam"
import { IconRefresh } from "../../../images/xd/icons/IconRefresh"
import { LINKS } from "../../../routing/links"
import { useFinishStepAndNavigate } from "../../../routing/useStepsRouting"
import { FinalCheckClaimSection } from "../../../sections/finalCheck/FinalCheckClaimSection"
import { FinalCheckInfoBox } from "../../../sections/finalCheck/FinalCheckInfoBox"
import { FinalCheckSection } from "../../../sections/finalCheck/FinalCheckSection"
import { resolveEditLink } from "../../../sections/finalCheck/FinalCheckSection.utils"
import { FinalCheckVideoSection } from "../../../sections/finalCheck/FinalCheckVideoSection"
import { StepType } from "../../../utils/types"

export const Route = createFileRoute("/$sessionId/_session/final-check")({
  component: FinalCheck,
})

function FinalCheck() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const windowScrollState = useWindowScroll()

  const state = useNetworkState()
  const isOffline = !state.online

  const { sessionId } = Route.useParams()
  const { data: orderedSteps, isLoading: isLoadingOrderedSteps } = useOrderedSteps(sessionId)
  const { data: uploadStatus, isLoading: isLoadingUploadStatus } = useUploadStatus(sessionId)
  const finishAndNavigate = useFinishStepAndNavigate(sessionId)

  const finishSessionMutation = useFinishSession(sessionId)

  function onFinishSession() {
    finishSessionMutation.mutate(undefined, {
      onSuccess: () => finishAndNavigate("FinalCheck"),
      onError: (error) => {
        logger.error(`Failed to finish session ${sessionId}`, error)
      },
    })
  }

  /* Loading state */
  if (
    finishSessionMutation.isPending ||
    isLoadingOrderedSteps ||
    isLoadingUploadStatus ||
    !orderedSteps ||
    !uploadStatus
  ) {
    return <SpinnerLayout />
  }

  const {
    hasFailedPhotos,
    hasUploadingPhotos,
    hasAllUploaded,
    stepsWithMissingPhotos,
    hasUploadingVideos,
    hasFailedVideos,
    stepsWithMissingVideos,
    hasUploadingClaim,
    hasFailedClaim,
    hasIncompleteClaimStep,
  } = uploadStatus

  const realSteps = orderedSteps.filter(isRealOrderedStep)
  const hasSteps = (stepType: StepType) => realSteps.some((s) => s.stepType === stepType)

  const firstStepWithMissingPhotos = stepsWithMissingPhotos[0]
  const firstStepWithMissingPhotosLink = firstStepWithMissingPhotos
    ? resolveEditLink({
        stepType: firstStepWithMissingPhotos.stepType,
        sessionId,
        type: firstStepWithMissingPhotos.name,
      })
    : undefined

  const firstStepWithMissingVideos = stepsWithMissingVideos[0]
  const firstStepWithMissingVideosLink = firstStepWithMissingVideos
    ? resolveEditLink({
        stepType: firstStepWithMissingVideos.stepType,
        sessionId,
        type: firstStepWithMissingVideos.name,
      })
    : undefined
  // Mandatory Claim step with no markers left counts as missing media (send it to capture a new one)
  const claimIncompleteLink = hasIncompleteClaimStep ? LINKS.claimNew(sessionId) : undefined
  const firstStepWithMissingMediaLink =
    firstStepWithMissingPhotosLink ?? firstStepWithMissingVideosLink ?? claimIncompleteLink

  const isPhotosReadyToFinalize = !hasFailedPhotos && !hasUploadingPhotos && !isOffline && hasAllUploaded
  const isVideosReadyToFinalize = !hasFailedVideos && !hasUploadingVideos && !isOffline && hasAllUploaded
  const isClaimReadyToFinalize = !hasFailedClaim && !hasUploadingClaim && !isOffline && hasAllUploaded
  const isReadyToFinalize =
    isPhotosReadyToFinalize && isVideosReadyToFinalize && isClaimReadyToFinalize && !hasIncompleteClaimStep

  function handleResetUploads() {
    resetFailedUploads()
  }

  function renderInfoBox() {
    if (isOffline) {
      return <FinalCheckInfoBox text={t("overview.needsInternet")} />
    }

    if (hasFailedPhotos || hasFailedVideos || hasFailedClaim) {
      return (
        <FinalCheckInfoBox
          text={
            hasFailedPhotos || hasFailedClaim
              ? t("overview.photosFailedToUpload.text")
              : t("overview.videosFailedToUpload.text")
          }
          buttonText={t("overview.photosFailedToUpload.button")}
          buttonOnClick={handleResetUploads}
          buttonIcon={<IconRefresh className="size-6 text-white" />}
        />
      )
    }

    const downlink = state.downlink
    if (isNotNil(downlink) && (hasUploadingPhotos || hasUploadingVideos || hasUploadingClaim) && downlink <= 0.5) {
      return <FinalCheckInfoBox text={t("overview.slowInternet")} />
    }

    if (hasUploadingPhotos || hasUploadingVideos || hasUploadingClaim) {
      return <FinalCheckInfoBox text={t("overview.uploading")} />
    }

    if ((!hasAllUploaded || hasIncompleteClaimStep) && firstStepWithMissingMediaLink) {
      return (
        <FinalCheckInfoBox
          text={t("overview.missingSteps.text")}
          buttonText={t("overview.missingSteps.button")}
          buttonOnClick={() => void navigate({ to: firstStepWithMissingMediaLink })}
          buttonIcon={<IconCam className="size-6 text-white" />}
        />
      )
    }

    return null
  }

  function renderButton() {
    const buttonStyles = { variant: "primary", className: "fixed right-10 bottom-10 w-fit" } as const

    if (isOffline) {
      return null
    }

    if (hasFailedPhotos || hasFailedVideos || hasFailedClaim) {
      return (
        <Button {...buttonStyles} data-testid="final-check-retry-btn" onClick={handleResetUploads}>
          {t("overview.button.retryUpload")}
        </Button>
      )
    }

    if (hasUploadingPhotos || hasUploadingVideos || hasUploadingClaim) {
      return (
        <Button {...buttonStyles} data-testid="final-check-uploading-btn" disabled>
          {t("overview.button.uploadingPhotos")}
        </Button>
      )
    }

    if ((!hasAllUploaded || hasIncompleteClaimStep) && firstStepWithMissingMediaLink) {
      return (
        <Button
          {...buttonStyles}
          data-testid="final-check-continue-btn"
          onClick={() => void navigate({ to: firstStepWithMissingMediaLink })}
        >
          {t("overview.button.continueTakingPhotos")}
        </Button>
      )
    }

    return (
      <Button
        {...buttonStyles}
        data-testid="final-check-send-btn"
        disabled={!isReadyToFinalize}
        onClick={onFinishSession}
      >
        {t("overview.button.finishSession")}
      </Button>
    )
  }

  const infoBox = renderInfoBox()

  return (
    <>
      <BaseLayout hideCounter dataTestId="final-check-page">
        <div className="h-vh-header mx-auto max-w-150">
          {infoBox && <div className="h-4 w-4 shrink-0" />}
          {infoBox}

          <div className="h-4 w-4 shrink-0" />

          {hasSteps("Claim") && (
            <>
              <FinalCheckClaimSection />

              <div className="h-6 w-6 shrink-0" />
            </>
          )}

          {hasSteps("VehiclePhoto") && (
            <>
              <FinalCheckSection stepType="VehiclePhoto" photosEditable />

              <div className="h-4 w-4 shrink-0" />
            </>
          )}

          {hasSteps("Damage") && (
            <>
              <FinalCheckSection
                stepType="Damage"
                multiplePhotosPerStep
                photosEditable
                newPhotoLink={LINKS.damageNew(sessionId)}
              />

              <div className="h-4 w-4 shrink-0" />
            </>
          )}

          {hasSteps("ExtraEquipment") && (
            <>
              <FinalCheckSection
                stepType="ExtraEquipment"
                multiplePhotosPerStep
                newPhotoLink={LINKS.extraEquipmentSelect(sessionId)}
              />

              <div className="h-4 w-4 shrink-0" />
            </>
          )}

          {hasSteps("DocumentPhoto") && (
            <FinalCheckSection stepType="DocumentPhoto" photosEditable multiplePhotosPerStep />
          )}

          {hasSteps("VehicleVideo") && <FinalCheckVideoSection editable />}

          <div className="h-28 w-28 shrink-0" />
        </div>

        {renderButton()}
      </BaseLayout>

      <AnimatePresence>
        {windowScrollState.y > 200 && (
          <motion.div
            transition={{ ease: "easeInOut", duration: 0.3 }}
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            className="fixed top-0 right-0 left-0"
          >
            {infoBox}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
